import { useEffect, useMemo, useRef } from 'react';
import { CHATBOT_CONSTANTS } from '../constants/chatbot';

/**
 * Delay in milliseconds to allow content rendering before auto-scrolling
 */
const SCROLL_DELAY_MS = 150;

/**
 * Hook to handle mobile-specific chatbot behavior
 *
 * Manages:
 * - Body scroll locking when modal is open on mobile devices
 * - Auto-scrolling thread viewport to bottom on mount
 * - Mobile fullscreen layout (handled via CSS media queries)
 *
 * @param isModalOpen - Whether the chatbot modal is currently open
 * @returns Object containing mobile styles and viewport ref callback for thread auto-scroll
 */
export function useMobileChatbot(isModalOpen: boolean) {
  const viewportRef = useRef<HTMLDivElement | null>(null);

  // Check if current viewport is mobile/tablet size (memoized to avoid duplicate calculations)
  const isMobile = useMemo(
    () => window.innerWidth <= CHATBOT_CONSTANTS.BREAKPOINTS.MD,
    [],
  );

  // Prevent body scroll on mobile when modal is open (iOS Safari compatible)
  useEffect(() => {
    if (!isMobile) {
      return;
    }

    if (isModalOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;

      // Add scroll lock class and set scroll position
      document.body.classList.add('chatbot-modal-open');
      document.body.style.top = `-${scrollY}px`;

      return () => {
        // Remove scroll lock class
        document.body.classList.remove('chatbot-modal-open');
        document.body.style.removeProperty('top');

        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isModalOpen, isMobile]);

  // Auto-scroll thread viewport to bottom on mount for mobile devices
  useEffect(() => {
    if (isMobile && viewportRef.current) {
      // Use setTimeout to ensure content is rendered before scrolling
      setTimeout(() => {
        if (viewportRef.current) {
          viewportRef.current.scrollTop = viewportRef.current.scrollHeight;
        }
      }, SCROLL_DELAY_MS);
    }
  }, [isMobile]);

  // Ref callback to capture the thread viewport element
  const setViewportRef = (element: HTMLDivElement | null) => {
    viewportRef.current = element;
  };

  // Generate mobile responsive styles with static viewport units
  const mobileStyles = `
    /* Prevent body scroll when chatbot modal is open on mobile (iOS Safari compatible) */
    @media (max-width: ${CHATBOT_CONSTANTS.BREAKPOINTS.MD}px) {
      body.chatbot-modal-open {
        overflow: hidden !important;
        position: fixed !important;
        width: 100% !important;
      }
    }

    /* Mobile and tablet: Full screen with static viewport units */
    @media (max-width: ${CHATBOT_CONSTANTS.BREAKPOINTS.MD}px) {
      [data-radix-popper-content-wrapper][data-modern-ui-react] {
        transform: translate(0px, 0px) !important;
        left: 0px !important;
        top: 0px !important;
        z-index: 10000002 !important;
      }

      [data-radix-popper-content-wrapper][data-modern-ui-react] > div[role="dialog"] {
        height: 100% !important;
        height: 100dvh !important;
        width: 100% !important;
        width: 100dvw !important;
        border-radius: 0 !important;
      }

      /* Prevent iOS auto-zoom on input focus by ensuring 16px minimum font size */
      [data-radix-popper-content-wrapper][data-modern-ui-react] input,
      [data-radix-popper-content-wrapper][data-modern-ui-react] textarea {
        font-size: 16px !important;
        min-height: 40px;
      }
    }
  `;

  return {
    mobileStyles,
    setViewportRef,
  };
}
